import Decimal from 'decimal.js';
import { SystemEntity } from '@erp/shared-types';
import type {
  AggregateAPInvoiceLineItemsInput,
  SalesOrderLineItem,
  TransferOrderLineItem,
} from '@erp/rpc';
import type { RelatedDocumentGroup } from './relatedLinesSummary';

export type AggregationTarget = AggregateAPInvoiceLineItemsInput['target'];

/** A related record the selected lines can be aggregated onto. */
export interface AggregationTargetOption {
  key: string;
  target: AggregationTarget;
  label: string | null;
  detail: string | null;
  /** Signed amount as a canonical string; null for a record with no price (a transfer order line). */
  amount: string | null;
  documentType: SystemEntity;
  /**
   * Line numbers of other invoice lines already matched to this record. The
   * server refuses to aggregate onto it until they are unmatched.
   */
  blockedByLineNumbers: number[];
}

const TARGET_KIND_BY_ENTITY: Partial<
  Record<SystemEntity, AggregationTarget['kind']>
> = {
  [SystemEntity.CHARGE]: 'CHARGE',
  [SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM]: 'PO_LINE',
  [SystemEntity.AR_SALES_ORDER_LINE_ITEM]: 'SO_LINE',
  [SystemEntity.IM_TRANSFER_ORDER_LINE_ITEM]: 'TO_LINE',
};

/**
 * The records of the invoice's related documents that a group of lines can be
 * aggregated onto: active charges and order lines, in document order.
 */
export function listAggregationTargets(
  groups: readonly RelatedDocumentGroup[],
  selectedLineNumbers: ReadonlySet<number>,
): AggregationTargetOption[] {
  return groups.flatMap((group) =>
    group.rows.flatMap((row) => {
      const kind = TARGET_KIND_BY_ENTITY[row.entityType];
      if (!kind || row.inactiveReason) return [];
      return [
        {
          key: row.key,
          target: { kind, id: row.entityId },
          label: row.label,
          detail: row.detail,
          amount: row.amount,
          documentType: group.entityType,
          blockedByLineNumbers: row.matchedInvoiceLineNumbers.filter(
            (lineNumber) => !selectedLineNumbers.has(lineNumber),
          ),
        },
      ];
    }),
  );
}

export interface AggregationAssessment {
  /** Sum of the selected line amounts, or null while any is not a number. */
  selectedTotal: string | null;
  /** Target amount minus the selected total, or null when either is unknown. */
  difference: string | null;
  /** The selected lines add up to the target to the cent. */
  exact: boolean;
}

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

function readDecimal(
  value: string | number | null | undefined,
): Decimal | null {
  const text = String(value ?? '').trim();
  return DECIMAL_PATTERN.test(text) ? new Decimal(text) : null;
}

/** How the selected lines' amounts compare to the chosen target. */
export function assessAggregation(
  lineTotals: ReadonlyArray<string | number | null | undefined>,
  targetAmount: string | null,
): AggregationAssessment {
  const amounts = lineTotals.map(readDecimal);
  const selectedTotal = amounts.every((amount) => amount !== null)
    ? Decimal.sum(0, ...(amounts as Decimal[]))
    : null;
  const target = targetAmount === null ? null : readDecimal(targetAmount);
  const difference =
    selectedTotal && target ? target.minus(selectedTotal) : null;
  return {
    selectedTotal: selectedTotal?.toFixed(2) ?? null,
    difference: difference?.toFixed(2) ?? null,
    exact: difference?.abs().lt('0.005') ?? false,
  };
}

export interface RankedAggregationTarget extends AggregationTargetOption {
  /** How the selected lines compare to this record. */
  assessment: AggregationAssessment;
}

/**
 * Each record with how far the selected lines' total is from it, closest
 * first. Records another invoice line already holds sort last because they
 * cannot be picked, then records with no amount to compare. Ties keep their
 * original order.
 */
export function rankAggregationTargets(
  options: readonly AggregationTargetOption[],
  lineTotals: ReadonlyArray<string | number | null | undefined>,
): RankedAggregationTarget[] {
  const distance = (item: RankedAggregationTarget): Decimal | null => {
    const difference = readDecimal(item.assessment.difference);
    return difference ? difference.abs() : null;
  };
  return options
    .map((option, index) => ({
      item: {
        ...option,
        assessment: assessAggregation(lineTotals, option.amount),
      },
      index,
    }))
    .sort((a, b) => {
      const blocked =
        Number(a.item.blockedByLineNumbers.length > 0) -
        Number(b.item.blockedByLineNumbers.length > 0);
      if (blocked !== 0) return blocked;
      const da = distance(a.item);
      const db = distance(b.item);
      if (da && db) return da.comparedTo(db) || a.index - b.index;
      if (da) return -1;
      if (db) return 1;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

export type TargetListState =
  'loading' | 'no-documents' | 'no-targets' | 'ready';

/** What the target list shows: records, or the reason there are none. */
export function targetListState(input: {
  isLoading: boolean;
  targetCount: number;
  hasDocuments: boolean;
}): TargetListState {
  if (input.isLoading) return 'loading';
  if (input.targetCount > 0) return 'ready';
  return input.hasDocuments ? 'no-targets' : 'no-documents';
}

interface BuildAggregatePayloadInput {
  invoiceId: string;
  lineItemIds: readonly string[];
  target: AggregationTarget;
  commonDescription: string;
}

export function toAggregatePayload(
  input: BuildAggregatePayloadInput,
): AggregateAPInvoiceLineItemsInput {
  const commonDescription = input.commonDescription.trim();
  return {
    apInvoiceId: input.invoiceId,
    lineItemIds: [...input.lineItemIds],
    target: input.target,
    ...(commonDescription ? { commonDescription } : {}),
  };
}

/** The lines of one related order, as loaded from its own endpoint. */
export interface OrderDocumentLines {
  documentType: SystemEntity;
  kind: 'SO_LINE' | 'TO_LINE';
  lines: Array<{
    id: string;
    label: string | null;
    detail: string | null;
    amount: string | null;
  }>;
}

function lineLabel(line: {
  productName?: string | null;
  description?: string | null;
  productSku?: string | null;
}): { label: string | null; detail: string | null } {
  const label = line.productName || line.description || line.productSku || null;
  const sku = line.productSku ?? null;
  return { label, detail: sku !== label ? sku : null };
}

export function salesOrderLines(
  lineItems: readonly SalesOrderLineItem[],
): OrderDocumentLines {
  return {
    documentType: SystemEntity.AR_SALES_ORDER,
    kind: 'SO_LINE',
    lines: lineItems.map((line) => ({
      id: line.id,
      ...lineLabel(line),
      amount: line.lineTotal,
    })),
  };
}

export function transferOrderLines(
  lineItems: readonly TransferOrderLineItem[],
): OrderDocumentLines {
  return {
    documentType: SystemEntity.IM_TRANSFER_ORDER,
    kind: 'TO_LINE',
    lines: lineItems.map((line) => ({
      id: line.id,
      ...lineLabel(line),
      amount: null,
    })),
  };
}

/**
 * The product lines of the invoice's related sales and transfer orders that a
 * group of lines can be aggregated onto. PO lines come with the related-lines
 * summary; these two order types load their lines separately.
 */
export function listOrderLineTargets(
  documents: readonly OrderDocumentLines[],
  matches: ReadonlyMap<string, number[]>,
  selectedLineNumbers: ReadonlySet<number>,
): AggregationTargetOption[] {
  return documents.flatMap((document) =>
    document.lines.map((line) => ({
      key: `line-item:${line.id}`,
      target: { kind: document.kind, id: line.id },
      label: line.label,
      detail: line.detail,
      amount: line.amount,
      documentType: document.documentType,
      blockedByLineNumbers: (matches.get(line.id) ?? []).filter(
        (lineNumber) => !selectedLineNumbers.has(lineNumber),
      ),
    })),
  );
}
